// PointFilter — input pipeline hook (architecture.md §19.10 C).
//
// MVP behaviour: identity (素通し). The filter exists so that β Modern
// Layout can drop in a smoothing / stabilisation filter without touching
// the engine plumbing.
//
// Shape: synchronous transform from one input point to zero or more output
// points. Returning `null` filters the point out. Returning `[]` is
// effectively the same; both are accepted to keep the β implementation
// space wide.
//
// (architecture.md §19.10 C / implementation-plan.md Phase 3)
//
// β Modern: `createStabilizerPointFilter` — stroke stabilisation (手ぶれ補正,
// modern-layout.md §9.8). Distance-resampling + EMA, causal / emit-as-you-go.
// `off` level returns the identity filter so the wiring stays bit-identical
// for Classic and for Modern with stabilisation disabled.

import type { StrokePoint } from '@oekaki/protocol';

/**
 * A `PointFilter` is a stateful transform that can fan-in multiple raw
 * pointer samples and emit cleaned points downstream. β smoothing filters
 * (e.g. one-euro / EMA) live here.
 *
 * MVP supplies the identity filter; β will swap implementations.
 */
export interface PointFilter {
  /** Begin a new stroke. The filter resets any internal history. */
  reset(): void;
  /** Feed one raw input point. Returns 0+ filtered points to render. */
  push(point: StrokePoint): readonly StrokePoint[];
  /** Stroke is ending — flush any buffered tail samples. */
  flush(): readonly StrokePoint[];
}

/**
 * Identity PointFilter — passes raw points through unchanged.
 * MVP default; replace at engine construction time when β ships.
 */
export const createIdentityPointFilter = (): PointFilter => {
  return {
    reset() {
      /* no state */
    },
    push(point) {
      return [point];
    },
    flush() {
      return [];
    },
  };
};

// ─── β Modern — stroke stabilisation (手ぶれ補正, modern-layout.md §9.8) ──────

/**
 * Stabiliser strength. `off` is bit-identical to the identity filter so
 * Classic / disabled-Modern strokes are unchanged.
 *
 * Persisted per-user in localStorage by the frontend (penPressure 流儀).
 * Default `off`.
 */
export type StabilizerLevel = 'off' | 'weak' | 'medium' | 'strong';

/**
 * Per-level tuning (modern-layout.md §9.8).
 *
 * Two knobs, both grounded in the §9.8 "粒度依存" fix:
 *
 * - `spacing` (px) — **distance resampling** interval. The raw input is
 *   resampled to one point every `spacing` px BEFORE smoothing, so the EMA
 *   coefficient is applied once per fixed distance rather than once per raw
 *   sample. This removes the dependency on sampling density / pen speed /
 *   device polling rate (a high-Hz Apple Pencil and a low-Hz mouse converge
 *   to the same geometry → the same smoothed curve). Geometry-only ⇒ no
 *   timestamps ⇒ deterministic (= bit-perfect across client / server snapshot).
 *
 * - `alpha` — **EMA** coefficient applied at each resampled step:
 *   `out = alpha·resampled + (1 - alpha)·prevOut`. Smaller alpha ⇒ stronger
 *   smoothing & more lag.
 *
 * (要検証) Values are first-pass defaults to be tuned against real-device
 * feel (§9.8 「具体値は実機 feel で要調整」). Stronger = larger spacing +
 * smaller alpha (both push toward a smoother, laggier line).
 */
interface StabilizerTuning {
  /** Distance-resampling interval in canvas px. */
  readonly spacing: number;
  /** EMA coefficient in (0, 1]. */
  readonly alpha: number;
}

// (要検証) feel-tunable constants. Kept as a single table so the frontend /
// designer can adjust strengths in one place.
const STABILIZER_TUNING: Record<Exclude<StabilizerLevel, 'off'>, StabilizerTuning> = {
  weak: { spacing: 3, alpha: 0.5 },
  medium: { spacing: 5, alpha: 0.3 },
  strong: { spacing: 8, alpha: 0.15 },
};

/**
 * Linear-interpolate the *position* of two stroke points at parameter `t`
 * (0 → a, 1 → b). Pressure axes (`p` / `pAlpha`) are interpolated linearly
 * too when both endpoints carry them; otherwise the field is taken from the
 * point we are stepping toward (nearest), and omitted when neither has it.
 *
 * §9.8 says "pressure は素通し" — we never *smooth* pressure with the EMA;
 * we only carry it along the resampled geometry so width/alpha-pressure pens
 * still get a sensible pressure at each emitted point. Pressure dynamics are
 * preserved (no temporal averaging), matching the §9.8 intent.
 */
const lerpPoint = (a: StrokePoint, b: StrokePoint, t: number): StrokePoint => {
  const out: StrokePoint = {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
  };
  const p = lerpPressure(a.p, b.p, t);
  if (p !== undefined) out.p = p;
  const pAlpha = lerpPressure(a.pAlpha, b.pAlpha, t);
  if (pAlpha !== undefined) out.pAlpha = pAlpha;
  return out;
};

const lerpPressure = (
  a: number | undefined,
  b: number | undefined,
  t: number,
): number | undefined => {
  if (a !== undefined && b !== undefined) return a + (b - a) * t;
  // Only one endpoint carries pressure → take the value we are stepping
  // toward when past the midpoint, else the one we came from (nearest).
  if (b !== undefined) return t >= 0.5 ? b : a;
  if (a !== undefined) return t < 0.5 ? a : b;
  return undefined;
};

const dist = (ax: number, ay: number, bx: number, by: number): number => {
  const dx = bx - ax;
  const dy = by - ay;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Stabiliser PointFilter — **distance resampling + EMA** (modern-layout.md §9.8).
 *
 * Pipeline per stroke:
 *
 *   raw input  ──resample to `spacing` px──▶  EMA(alpha) on position  ──▶  emit
 *
 * Properties (all §9.8 requirements):
 *
 * - **Causal / emit-as-you-go**: every emitted point is final the instant it
 *   is returned — past output is never rewritten. So `push` can return its
 *   points immediately and the caller may send them straight away ("send =
 *   commit", §9.8). No look-ahead / non-causal buffering (future extension).
 *
 * - **Density-independent**: smoothing strength depends only on travelled
 *   distance, not on how many raw samples arrived → identical feel across
 *   devices / pen speeds / rAF batching (§9.8 粒度依存 fix).
 *
 * - **Deterministic**: geometry only, no timestamps. Same input → same output
 *   ⇒ bit-perfect across local render / peers / server snapshot.
 *
 * - **First point is an anchor**: the very first point after `reset()` is
 *   emitted immediately and seeds the EMA, so the line starts exactly at the
 *   pen-down position (no lead-in lag).
 *
 * - **`flush()` converges to the real endpoint**: strong smoothing lags
 *   behind the cursor, so the EMA output stops short of the true pen-lift
 *   position. `flush` emits a short tail that walks the smoothed output to
 *   the last raw input point, so the rendered line reaches the lift position.
 *
 * `off` is handled by the engine returning the identity filter; this factory
 * is only constructed for weak/medium/strong.
 */
export const createStabilizerPointFilter = (
  level: Exclude<StabilizerLevel, 'off'>,
): PointFilter => {
  const tuning = STABILIZER_TUNING[level];
  const spacing = tuning.spacing;
  const alpha = tuning.alpha;

  // Last raw point received (the resampling cursor's source endpoint).
  let lastRaw: StrokePoint | null = null;
  // Distance accumulated along the current raw segment that has NOT yet been
  // consumed by a resample step. When it crosses `spacing` we emit a sample.
  let carry = 0;
  // EMA state — last emitted *smoothed* position. Pressure rides along the
  // resampled point, not the EMA, so we only keep x/y here.
  let emaX = 0;
  let emaY = 0;
  let started = false;

  // Apply the EMA to a resampled point and update state. Returns the emitted
  // (smoothed-position, carried-pressure) point.
  const smooth = (resampled: StrokePoint): StrokePoint => {
    emaX = alpha * resampled.x + (1 - alpha) * emaX;
    emaY = alpha * resampled.y + (1 - alpha) * emaY;
    const out: StrokePoint = { x: emaX, y: emaY };
    if (resampled.p !== undefined) out.p = resampled.p;
    if (resampled.pAlpha !== undefined) out.pAlpha = resampled.pAlpha;
    return out;
  };

  return {
    reset() {
      lastRaw = null;
      carry = 0;
      emaX = 0;
      emaY = 0;
      started = false;
    },

    push(point) {
      // First point: emit immediately as the anchor and seed the EMA so the
      // line starts exactly at pen-down (no lead-in lag).
      if (!started) {
        started = true;
        lastRaw = point;
        emaX = point.x;
        emaY = point.y;
        // Emit the raw first point verbatim (EMA of a single point = itself).
        return [point];
      }

      const prev = lastRaw as StrokePoint;
      const segLen = dist(prev.x, prev.y, point.x, point.y);
      lastRaw = point;
      if (segLen === 0) return [];

      const out: StrokePoint[] = [];
      // Standard arc-length resampling along the raw segment [prev → point].
      // `carry` = distance already travelled since the previous resample
      // point (carried over from earlier segments). Measured from that
      // previous resample point, samples land at multiples of `spacing`, i.e.
      // at distances `spacing - carry`, `2·spacing - carry`, … *into* this
      // segment. The new carry is simply `(carry + segLen) mod spacing`.
      let sampleDist = spacing - carry;
      while (sampleDist <= segLen) {
        const t = sampleDist / segLen;
        out.push(smooth(lerpPoint(prev, point, t)));
        sampleDist += spacing;
      }
      carry = (carry + segLen) % spacing;
      return out;
    },

    flush() {
      // Converge the smoothed output to the true pen-lift position so the
      // line reaches the cursor. Emit a short tail that walks the EMA from
      // its current lagged position to `lastRaw` (§9.8 flush 収束).
      if (!started || lastRaw === null) {
        return [];
      }
      const target = lastRaw;
      const out: StrokePoint[] = [];
      // A handful of EMA steps toward the fixed target collapses the lag.
      // Bounded iteration keeps it deterministic and short.
      const MAX_TAIL = 16;
      for (let i = 0; i < MAX_TAIL; i++) {
        const remaining = dist(emaX, emaY, target.x, target.y);
        if (remaining < 0.5) break;
        out.push(smooth(target));
      }
      // Final exact landing on the lift position so the line truly ends there.
      const tail: StrokePoint = { x: target.x, y: target.y };
      if (target.p !== undefined) tail.p = target.p;
      if (target.pAlpha !== undefined) tail.pAlpha = target.pAlpha;
      emaX = target.x;
      emaY = target.y;
      out.push(tail);
      return out;
    },
  };
};

/**
 * Build the PointFilter for a stabiliser level. `off` ⇒ identity (bit-
 * identical), otherwise the distance-resampling + EMA stabiliser.
 */
export const createPointFilterForLevel = (level: StabilizerLevel): PointFilter =>
  level === 'off' ? createIdentityPointFilter() : createStabilizerPointFilter(level);
